import {
	AfterViewInit,
	Component,
	ContentChild,
	ElementRef,
	OnDestroy,
	TemplateRef,
	ViewChild,
	effect,
	input,
} from '@angular/core';
import * as echarts from 'echarts';
import { ECharts } from 'echarts';

interface StackedVerticalBarChartTooltipModel {
	name: string;
	series: string;
	value: number;
	color?: string;
}

@Component({
	selector: 'ae-stacked-vertical-bar-chart',
	imports: [],
	templateUrl: './stacked-vertical-bar-chart.component.html',
	styleUrl: './stacked-vertical-bar-chart.component.scss',
})
export class StackedVerticalBarChartComponent
	implements AfterViewInit, OnDestroy
{
	private chart?: ECharts;
	private resizeObserver?: ResizeObserver;
	@ContentChild('tooltipTemplate', { read: TemplateRef })
	private tooltipTemplate?: TemplateRef<{
		model: StackedVerticalBarChartTooltipModel;
	}>;

	@ViewChild('stackedVerticalBarChart', { static: true })
	private stackedVerticalBarChartRef!: ElementRef<HTMLElement>;

	@ViewChild('stackedVerticalBarChartContainer', { static: true })
	private stackedVerticalBarChartContainerRef!: ElementRef<HTMLElement>;

	customColors = input.required<{ name: string; value: string }[]>();
	legendPosition = input<'Below' | 'Right'>('Below');
	/**
	 * 					[scheme]="{
						name: null,
						selectable: false,
						group: null,
						domain: colors,
					}"
	 */
	scheme = input.required<any>();
	results = input.required<any>();
	legend = input.required<boolean>();
	view = input.required<[number, number]>();
	xAxis = input.required<boolean>();
	yAxis = input.required<boolean>();

	constructor() {
		effect(() => {
			this.customColors();
			this.results();

			this.updateChart();
		});
	}

	ngAfterViewInit(): void {
		this.chart = this.initChart(this.stackedVerticalBarChartRef.nativeElement);
		this.updateChart();
		this.resizeChart();

		this.resizeObserver = new ResizeObserver(() => {
			this.resizeChart();
		});
		this.resizeObserver.observe(
			this.stackedVerticalBarChartContainerRef.nativeElement,
		);
	}

	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
		this.chart?.dispose();
	}

	protected getChartDimensions(): [number, number] {
		if (!this.view()) {
			return [200, 200];
		}

		return [this.view()[0] ?? 200, this.view()[1] ?? 200];
	}

	protected getColor(index: number): string {
		return this.customColors()[index]?.value || 'black';
	}

	protected initChart(element: HTMLElement): ECharts {
		return echarts.init(element);
	}

	private updateChart(): void {
		if (!this.chart) {
			return;
		}

		const results = [...this.results()];
		const colors = this.customColors();
		const yMax = this.yScaleMaxBasedOnData();

		this.chart.setOption({
			tooltip: {
				trigger: 'item',
				backgroundColor: 'var(--dark)',
				textStyle: {
					color: 'var(--light)',
				},
				formatter: this.tooltipTemplate
					? (params: {
							name: string;
							seriesName: string;
							value: number;
							color?: string;
						}) => this.renderTooltipTemplate(params)
					: undefined,
			},
			color: colors.map((color) => color.value),
			legend: {
				left: 'left',
				data: colors.map((color) => color.name),
			},
			grid: {
				right: '4%',
				bottom: '12%',
				height: '80%',
				width: '95%',
				containLabel: true,
			},
			xAxis: {
				type: 'category',
				data: results.map((item) => item.name),
				axisLabel: {
					interval: 0,
					hideOverlap: false,
				},
			},
			yAxis: {
				type: 'value',
				max: yMax,
				// axisLabel: {
				// 	formatter: (value: number) => {
				// 		return value * this.yScaleMax();
				// 	},
				// },
			},
			series: colors.map((color) => ({
				name: color.name,
				type: 'bar',
				stack: 'total',
				emphasis: {
					focus: 'series',
				},
				data: results.map((item) => {
					const seriesItem = item.series.find(
						(series) => series.name === color.name,
					);
					return seriesItem ? seriesItem.value : 0;
				}),
			})),
		});
	}

	private renderTooltipTemplate(params: {
		name: string;
		seriesName: string;
		value: number;
		color?: string;
	}): string {
		if (!this.tooltipTemplate) {
			return '';
		}

		const view = this.tooltipTemplate.createEmbeddedView({
			model: {
				name: params.seriesName,
				series: params.name,
				value: params.value,
				color: params.color,
			},
		});

		view.detectChanges();

		const container = document.createElement('div');
		for (const rootNode of view.rootNodes) {
			container.append(rootNode.cloneNode(true));
		}

		view.destroy();

		return container.innerHTML;
	}

	private resizeChart(): void {
		const element = this.stackedVerticalBarChartRef.nativeElement;
		this.chart?.resize({
			width: element.clientWidth,
			height: element.clientHeight,
		});
	}

	private yScaleMaxBasedOnData = (): number => {
		const results = [...this.results()];
		let max = 0;
		results.forEach((item) => {
			item.series.forEach((ser) => {
				if (ser.value > max) {
					max = ser.value;
				}
			});
		});
		if (max < 10) {
			max = 10;
		} else {
			max = Math.ceil(max / 10) * 10;
		}
		return max;
	};
}
